//
// Compose page: live per-line syllable counter (debounced POST /validate)
// plus a Publish flow that creates the haiku, saves it into the library, and
// redirects to the haiku page.
//
// Every asserted/interactive element carries a stable `data-testid`
// (Playwright selects via `get_by_test_id`, never `#id`/`.class`/text). The
// live counter is an `aria-live="polite"` region; the error region is
// `role="alert"` and always attached (hidden when there is nothing to show).
//
// `createHaiku` returns `{ok, status, data}` from `postJSON` -- it does NOT
// throw on a non-2xx response (e.g. 422 invalid meter), so we must check
// `res.ok` rather than wrapping the call in try/catch.
//
// Task 6: a signed-in principal's `default_visibility` (Settings) governs
// whether the freshly-published poem is immediately saved. Every new node
// is created `ephemeral` (Task-4 server behavior); this reads `GET /me`
// right after create and only calls `saveHaiku` when the caller is signed
// out (unchanged prior behavior -- always save) or explicitly prefers
// `"saved"`. A signed-in principal with `default_visibility: "ephemeral"`
// gets to keep the freshly-created draft as ephemeral instead of it being
// force-saved.
//
import { validate, createHaiku, saveHaiku, getJSON } from "/app/assets/api.js";
import { buildStylePanel } from "/app/assets/style-panel.js";
import type { Principal } from "/app/assets/types.js";

const lineIds = ["line-1", "line-2", "line-3"];
const lineInputs = lineIds.map((id) => document.getElementById(id) as HTMLInputElement);
const form = document.querySelector('[data-testid="compose-form"]') as HTMLFormElement;
const counter = document.querySelector('[data-testid="syllable-counter"]') as HTMLElement;
const errorEl = document.querySelector('[data-testid="compose-error"]') as HTMLElement;
const submitButton = document.querySelector('[data-testid="compose-submit"]') as HTMLButtonElement;
const tagsInput: HTMLInputElement | null = document.querySelector('[data-testid="compose-tags"]');

const lines = (): string[] => lineInputs.map((el) => el.value);

// Split on commas and/or whitespace -- the server (`tags.normalize_tags`) does
// the real normalization (lowercase, char filter, dedupe, caps); this just
// turns the free-text field into an array of candidate tags.
function tags(): string[] {
  const raw = tagsInput ? tagsInput.value : "";
  return raw
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function showError(text: string): void {
  errorEl.textContent = text;
  errorEl.hidden = false;
}

function clearError(): void {
  errorEl.textContent = "";
  errorEl.hidden = true;
}

// ---- typing lands in the pond ------------------------------------------
// Each keystroke sends a small ripple into the fx canvas at the caret's
// approximate x (measured text width), and a line that just reached its
// 5/7/5 count drops a spot of ink at its end — writing disturbs the same
// water the finished poems float on. Both route through window.HaikuRipple
// (absent = no fx page boot; reduce = the loop never runs), so this is
// purely additive.
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const measureCtx = document.createElement("canvas").getContext("2d");
let lastSplash = 0;

function lineEndPoint(input: HTMLInputElement): { x: number; y: number } {
  const rect = input.getBoundingClientRect();
  let textW = 8;
  if (measureCtx) {
    measureCtx.font = getComputedStyle(input).font;
    textW = measureCtx.measureText(input.value).width + 8;
  }
  return { x: rect.left + Math.min(textW, rect.width - 8), y: rect.top + rect.height / 2 };
}

function splashAt(input: HTMLInputElement): void {
  if (reduceMotion || !window.HaikuRipple) return;
  const now = performance.now();
  if (now - lastSplash < 90) return; // held-key repeat shouldn't churn the pond
  lastSplash = now;
  const p = lineEndPoint(input);
  window.HaikuRipple.splash(p.x, p.y, 0.5);
}

const SYLLABLE_TARGETS = [5, 7, 5];
let lineWasOnCount = [false, false, false];

// ---- syllables as moon phases -------------------------------------------
// The counter keeps its plain "5,7,5 · valid" text (aria-live reads it, tests
// assert it) but leads with three moons — one per line, waxing with the
// count, full and glowing on target, vermilion past it. Progress is felt at
// a glance before it's read.
const WAXING = ["○", "◔", "◑", "◕"];

function moonFor(count: number, target: number): { glyph: string; state: string } {
  if (!Number.isFinite(count) || count <= 0) return { glyph: "○", state: "empty" };
  if (count === target) return { glyph: "●", state: "full" };
  if (count > target) return { glyph: "●", state: "over" };
  return { glyph: WAXING[Math.min(3, Math.max(1, Math.round((count / target) * 4)))], state: "waxing" };
}

function renderCounter(syllables: string, valid: boolean): void {
  const counts = syllables.split(",").map((s) => Number(s.trim()));
  const moons = document.createElement("span");
  moons.className = "syllable-moons";
  moons.setAttribute("aria-hidden", "true");
  SYLLABLE_TARGETS.forEach((target, i) => {
    const phase = moonFor(counts[i] ?? Number.NaN, target);
    const moon = document.createElement("span");
    moon.className = "syllable-moon";
    moon.dataset.state = phase.state;
    moon.textContent = phase.glyph;
    moons.append(moon);
  });
  counter.textContent = "";
  counter.append(moons, document.createTextNode(`${syllables} · ${valid ? "valid 5-7-5" : "not yet 5-7-5"}`));
  counter.dataset.valid = String(valid);
}

// A line crossing onto its target count gets one drop of ink; staying there
// while the poet edits elsewhere stays quiet.
function inkOnNewlyCorrectLines(syllables: string): void {
  const counts = syllables.split(",").map((s) => Number(s.trim()));
  const onCount = SYLLABLE_TARGETS.map((t, i) => counts[i] === t);
  if (!reduceMotion && window.HaikuRipple) {
    onCount.forEach((ok, i) => {
      if (ok && !lineWasOnCount[i]) {
        const p = lineEndPoint(lineInputs[i]);
        window.HaikuRipple?.inkDrop(p.x, p.y);
      }
    });
  }
  lineWasOnCount = onCount;
}

let debounceTimer: ReturnType<typeof setTimeout> | undefined;
function scheduleValidate(): void {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    void (async () => {
      try {
        const result = await validate(lines());
        const syllables = result && result.syllables ? result.syllables : "–,–,–";
        const valid = Boolean(result && result.valid);
        renderCounter(syllables, valid);
        inkOnNewlyCorrectLines(syllables);
      } catch {
        counter.textContent = "–,–,–";
        counter.dataset.valid = "false";
      }
    })();
  }, 250);
}

for (const input of lineInputs) {
  input.addEventListener("input", scheduleValidate);
  input.addEventListener("input", () => splashAt(input));
}

// ---- card style panel -------------------------------------------------
// Shared with manage.ts's per-poem style editor (Task 5) --
// `style-panel.ts` owns the touched-flag contract: an untouched panel
// submits NO style at all (server stores NULL, card renders pure theme
// defaults).
const styleSlot = document.querySelector('[data-testid="style-panel-slot"]') as HTMLElement;
const stylePanel = buildStylePanel(styleSlot, { lines });

// the preview mirrors the poem as it's typed (line inputs already exist)
for (const input of lineInputs) input.addEventListener("input", () => stylePanel.refreshPreview());

form.addEventListener("submit", (event) => {
  event.preventDefault();
  void (async () => {
    clearError();
    submitButton.disabled = true;
    try {
      const res = await createHaiku(lines(), undefined, tags(), stylePanel.currentStyle());
      if (!res.ok) {
        showError(
          res.status === 422 ? "not a valid 5-7-5 haiku — adjust your lines and try again" : "could not publish"
        );
        return;
      }
      const id = res.data.id;
      const editToken = res.data.edit_token!;
      localStorage.setItem(`edit:${id}`, editToken);

      const me = await getJSON<Principal>("/me").catch((): Principal => ({}));
      const wantsEphemeral = Boolean(me && me.id && me.default_visibility === "ephemeral");
      if (!wantsEphemeral) await saveHaiku(id, editToken);

      location.href = `/app/haiku.html?id=${id}`;
    } catch {
      showError("could not publish");
    } finally {
      submitButton.disabled = false;
    }
  })();
});
